import PropTypes from 'prop-types';
import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';

// material-ui
import { makeStyles } from '@material-ui/styles';
import { Box, Collapse, IconButton, List, ListItemIcon, ListItemText, Tooltip, Typography } from '@material-ui/core';
import ListItemButton from '@material-ui/core/ListItemButton';

// project imports
import NavItem from './../NavItem';

// assets
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { IconChevronDown, IconChevronUp, IconCopy, IconCheck } from '@tabler/icons';

// style constant
const useStyles = makeStyles((theme) => ({
    collapseIcon: {
        fontSize: '1rem',
        marginTop: 'auto',
        marginBottom: 'auto'
    },
    collapseIconSub: {
        fontSize: '1rem',
        marginTop: 'auto',
        marginBottom: 'auto'
    },
    menuIcon: {
        marginTop: 'auto',
        marginBottom: 'auto'
    },
    listIcon: {
        minWidth: '18px',
        marginTop: 'auto',
        marginBottom: 'auto'
    },
    listCustomIconSub: {
        width: '6px',
        height: '6px'
    },
    listCustomIconSubActive: {
        width: '8px',
        height: '8px'
    },
    listItem: {
        marginBottom: '5px',
        alignItems: 'flex-start'
    },
    listItemNoBack: {
        marginBottom: '5px',
        backgroundColor: 'transparent !important',
        paddingTop: '8px',
        paddingBottom: '8px',
        alignItems: 'flex-start'
    },
    subMenuCaption: {
        ...theme.typography.subMenuCaption
    },
    collapseWrapper: {
        position: 'relative',
        '&:after': {
            content: "''",
            position: 'absolute',
            left: '32px',
            top: 0,
            height: '100%',
            width: '1px',
            opacity: 1,
            background: theme.palette.primary.light
        }
    }
}));

// Recursively check whether any descendant menu item's URL matches the current path.
// Used to derive the "active" state of a collapse from the URL instead of click history.
const hasActiveDescendant = (menu, pathname) => {
    if (!menu) return false;
    if (menu.url && (pathname === menu.url || pathname.startsWith(menu.url + '/'))) return true;
    if (Array.isArray(menu.children)) {
        return menu.children.some((child) => hasActiveDescendant(child, pathname));
    }
    return false;
};

//-----------------------|| SIDEBAR MENU LIST COLLAPSE ITEMS ||-----------------------//

const NavCollapse = ({ menu, level }) => {
    const classes = useStyles();
    const location = useLocation();

    // Active is purely URL-driven: the section is highlighted only while the user
    // is viewing one of its leaves. Click no longer toggles "selected".
    const isActiveByUrl = hasActiveDescendant(menu, location.pathname);

    // Local expand state. While `isActiveByUrl` is true the collapse is forced
    // open and the chevron is hidden, but we still keep `open` in sync so that
    // when the URL leaves this section the user's previous expand choice persists.
    const [open, setOpen] = React.useState(() => menu.defaultOpen || isActiveByUrl);

    // Auto-expand when navigation lands on one of this section's pages (e.g. a direct link to a
    // project's Suppliers page) — but this no longer locks the section open. The user can still
    // collapse it by hand afterward; the effect just won't have re-run to force it open again
    // unless isActiveByUrl itself changes (e.g. navigating to a different page within it).
    React.useEffect(() => {
        if (isActiveByUrl) setOpen(true);
    }, [isActiveByUrl]);

    const history = useHistory();
    const handleClick = () => {
        // A collapse that has its own `url` (the demo "Statement" section) navigates to that
        // page on click and stays expanded so its child stays visible. Collapses without a url
        // (project sections) just toggle open/closed, exactly as before.
        if (menu.url) {
            history.push(menu.url);
            setOpen(true);
            return;
        }
        setOpen((prev) => !prev);
    };

    const selected = isActiveByUrl;

    // Copy-to-clipboard for `copyable` rows (project name) — shown on hover instead of making
    // the title text itself selectable, since the whole row is also the expand/collapse click
    // target and dragging to select text there would fight with that.
    const [hovering, setHovering] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const handleCopy = (e) => {
        e.stopPropagation();
        const text = typeof menu.title === 'string' ? menu.title : '';
        if (!text || !navigator.clipboard) return;
        navigator.clipboard
            .writeText(text)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            })
            .catch(() => toast.error('Could not copy — try selecting the text manually.'));
    };

    // menu collapse & item
    const menus = (menu.children ?? []).map((item) => {
        switch (item.type) {
            case 'collapse':
                return <NavCollapse key={item.id} menu={item} level={level + 1} />;
            case 'item':
                return <NavItem key={item.id} item={item} level={level + 1} />;
            default:
                return (
                    <Typography key={item.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });

    const Icon = menu.icon;
    const menuIcon = menu.icon ? (
        <Box
            sx={{
                width: 30,
                height: 30,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: selected ? 'rgba(25,118,210,0.1)' : 'transparent',
                transition: 'background-color 0.15s ease',
            }}
        >
            <Icon
                stroke={1.5}
                size="1.35rem"
                style={{
                    color: selected ? '#1976d2' : 'rgba(58, 74, 92, 0.55)',
                    transition: 'color 0.15s ease',
                }}
            />
        </Box>
    ) : (
        <Box
            sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: selected ? '#1976d2' : 'transparent',
            }}
        />
    );

    return (
        <React.Fragment>
            <ListItemButton
                selected={selected}
                onClick={handleClick}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                className={level > 1 ? classes.listItemNoBack : classes.listItem}
                sx={{
                    borderRadius: 2,
                    mb: '2px',
                    ml: 0.5,
                    mr: 0.5,
                    py: 0.75,
                    color: selected ? '#1a2733' : '#3a4a5c',
                    backgroundColor: selected ? 'rgba(0,0,0,0.03)' : 'transparent',
                    borderLeft: selected ? '3px solid #1976d2' : '3px solid transparent',
                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.03)',
                    },
                }}
                style={{ paddingLeft: level * 23 + 12 + 'px' }}
            >
                <ListItemIcon sx={{ minWidth: 26, color: 'inherit' }}>{menuIcon}</ListItemIcon>
                <ListItemText
                    primary={
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: selected ? 600 : 400,
                                fontSize: '0.82rem',
                                color: 'inherit',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {menu.title}
                        </Typography>
                    }
                    secondary={
                        menu.caption && (
                            <Typography variant="caption" className={classes.subMenuCaption} display="block" gutterBottom>
                                {menu.caption}
                            </Typography>
                        )
                    }
                />
                {menu.copyable && (
                    <Tooltip title={copied ? 'Copied' : 'Copy name'}>
                        <IconButton
                            size="small"
                            onClick={handleCopy}
                            sx={{
                                opacity: hovering || copied ? 1 : 0,
                                transition: 'opacity 0.15s ease',
                                p: 0.4,
                                mr: 0.25,
                                color: copied ? '#1976d2' : '#8895a2',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
                            }}
                        >
                            {copied ? (
                                <IconCheck stroke={1.5} size="0.95rem" />
                            ) : (
                                <IconCopy stroke={1.5} size="0.95rem" />
                            )}
                        </IconButton>
                    </Tooltip>
                )}
                {open ? (
                    <IconChevronUp stroke={1.5} size="1rem" style={{ color: '#8895a2' }} />
                ) : (
                    <IconChevronDown stroke={1.5} size="1rem" style={{ color: '#8895a2' }} />
                )}
            </ListItemButton>
            {/* `open` alone (not `isActiveByUrl || open`) — the mount effect above already syncs
                `open` to true when navigation lands here, so this now honors a manual collapse
                afterward instead of forcing this section open for as long as the URL matches it. */}
            <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding className={classes.collapseWrapper}>
                    {menus}
                </List>
            </Collapse>
        </React.Fragment>
    );
};

NavCollapse.propTypes = {
    menu: PropTypes.object,
    level: PropTypes.number
};

export default NavCollapse;
